import { Eye, History, Plus, Sparkles, X } from 'lucide-react';
import { msg, t } from '@/lib/i18n';
import { useMe } from '@/lib/me';
import { useScope } from '@/lib/scope';
import { IconButton, Tooltip } from '@/ui';
import { AiOffState } from './AiOffState';
import s from './Assistant.module.css';
import { Composer } from './Composer';
import { type AssistantContextInput, useCurrentAssistantContext } from './context';
import { Conversation } from './Conversation';
import { setThread, setView, useAssistantState } from './store';
import { ThreadList } from './ThreadList';
import { useConversation } from './useConversation';

const TYPE_LABEL: Record<string, string> = { vehicle: msg('Vehicle'), driver: msg('Driver'), device: msg('Device'), group: msg('Group') };

function contextLabel(ctx: AssistantContextInput | null): string | null {
  if (!ctx) return null;
  const e = ctx.entities?.[0];
  if (!e?.label) return ctx.title;
  // A type without a label here would show its key ("company Demo Logistics"): the name alone says enough.
  const type = TYPE_LABEL[e.type];
  return type ? `${t(type)} ${e.label}` : e.label;
}

/** The assistant: context chip, threads, streamed conversation, composer. */
export function AssistantPanel({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const scope = useScope();
  const ctx = useCurrentAssistantContext();
  const { view, threadId } = useAssistantState();
  const projectId = scope.projectIds.length === 1 ? scope.projectIds[0] : undefined;
  const convo = useConversation(threadId, projectId);
  const label = contextLabel(ctx);
  const enabled = me.ai.enabled;

  return (
    <section className={s.panel} aria-label={t('Assistant')}>
      <header className={s.header}>
        <Sparkles className={s.brandIcon} aria-hidden />
        <h2 className={s.heading}>{t('Assistant')}</h2>
        {enabled && (
          <>
            <IconButton
              icon={History}
              size="sm"
              label={t('Conversations')}
              active={view === 'threads'}
              onClick={() => setView(view === 'threads' ? 'chat' : 'threads')}
            />
            <IconButton icon={Plus} size="sm" label={t('New conversation')} onClick={() => setThread(null)} />
          </>
        )}
        <IconButton icon={X} size="sm" label={t('Close assistant')} onClick={onClose} />
      </header>
      {label && (
        <div className={s.context}>
          <Tooltip content={t('The assistant sees what is on this page')}>
            <span className={s.contextChip} tabIndex={0}>
              <Eye aria-hidden />
              <span className={s.contextText}>{t('Looking at: {what}', { what: label })}</span>
            </span>
          </Tooltip>
        </div>
      )}
      <div className={s.body}>
        {!enabled ? (
          <AiOffState />
        ) : view === 'threads' ? (
          <ThreadList current={threadId} />
        ) : (
          <Conversation convo={convo} context={ctx} onPrompt={(p) => void convo.send(p)} />
        )}
      </div>
      {enabled && view === 'chat' && <Composer onSend={(text) => void convo.send(text)} onStop={convo.stop} streaming={convo.streaming} />}
    </section>
  );
}
